import { useState, useEffect } from "react";
import { DollarSign, Check, Clock, Info } from "lucide-react";
import { VentureCard, VentureCardHeader, VentureCardTitle, VentureCardContent } from "@/components/ui/venture-card";
import { BadgeVenture } from "@/components/ui/badge-venture";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

interface ClientRevShare {
  id: string;
  company_name: string;
  package: string;
  vh_attributed_lifetime_cents: number;
  rev_share_active: boolean;
  rev_share_approved_at: string | null;
}

const THRESHOLD_CENTS = 5000000; // $50,000

export function RevShareStatusCard() {
  const [clients, setClients] = useState<ClientRevShare[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("clients")
      .select("id, company_name, package, vh_attributed_lifetime_cents, rev_share_active, rev_share_approved_at")
      .eq("status", "active")
      .order("vh_attributed_lifetime_cents", { ascending: false });
    if (error) {
      toast.error("Failed to load rev share data");
    } else {
      setClients((data || []) as ClientRevShare[]);
    }
    setLoading(false);
  };

  const toggleRevShare = async (client: ClientRevShare) => {
    const newActive = !client.rev_share_active;
    const { error } = await supabase
      .from("clients")
      .update({
        rev_share_active: newActive,
        rev_share_approved_at: newActive ? new Date().toISOString() : null,
      })
      .eq("id", client.id);
    if (error) {
      toast.error("Failed to update rev share status");
    } else {
      toast.success(
        newActive
          ? `Rev share activated for ${client.company_name}`
          : `Rev share deactivated for ${client.company_name}`
      );
      fetchClients();
    }
  };

  const formatCurrency = (cents: number) =>
    `$${(cents / 100).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const progressPercent = (cents: number) => Math.min(100, (cents / THRESHOLD_CENTS) * 100);

  if (loading) return null;

  // Only show clients with any attributed revenue
  const relevantClients = clients.filter(c => c.vh_attributed_lifetime_cents > 0 || c.rev_share_active);
  if (relevantClients.length === 0) return null;

  return (
    <VentureCard>
      <VentureCardHeader>
        <VentureCardTitle className="flex items-center gap-2">
          <DollarSign className="w-5 h-5" />
          Revenue Share Status
          <TooltipProvider delayDuration={0}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Info className="w-3.5 h-3.5 text-muted-foreground cursor-help" />
              </TooltipTrigger>
              <TooltipContent className="max-w-xs">
                <p>Tracks lifetime Venture Hills-attributed revenue per client. Once a client exceeds $50,000 in attributed closes, an admin can activate the 7.5% monthly revenue share. Only deals where both the lead source AND setter are Venture Hills count toward attribution.</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </VentureCardTitle>
      </VentureCardHeader>
      <VentureCardContent>
        <div className="space-y-4">
          {relevantClients.map((client) => {
            const pastThreshold = client.vh_attributed_lifetime_cents >= THRESHOLD_CENTS;
            return (
              <div key={client.id} className="p-4 rounded-lg border border-border">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <span className="font-medium">{client.company_name}</span>
                    <span className="text-sm text-muted-foreground ml-2">({client.package})</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {client.rev_share_active ? (
                      <BadgeVenture variant="success">
                        <Check className="w-3 h-3 mr-1" />Active (7.5%)
                      </BadgeVenture>
                    ) : pastThreshold ? (
                      <BadgeVenture variant="warning">
                        <Clock className="w-3 h-3 mr-1" />Threshold Met
                      </BadgeVenture>
                    ) : (
                      <BadgeVenture variant="default">Below Threshold</BadgeVenture>
                    )}
                    {pastThreshold && (
                      <Button
                        size="sm"
                        variant={client.rev_share_active ? "outline" : "default"}
                        onClick={() => toggleRevShare(client)}
                      >
                        {client.rev_share_active ? "Deactivate" : "Activate Rev Share"}
                      </Button>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="flex-1 bg-muted rounded-full h-2">
                    <div
                      className="bg-primary rounded-full h-2 transition-all"
                      style={{ width: `${progressPercent(client.vh_attributed_lifetime_cents)}%` }}
                    />
                  </div>
                  <span className="text-sm font-medium whitespace-nowrap">
                    {formatCurrency(client.vh_attributed_lifetime_cents)} / {formatCurrency(THRESHOLD_CENTS)}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </VentureCardContent>
    </VentureCard>
  );
}
